import { useMemo } from 'react';

import { OutletContext } from '@typings';
import { usePlatformAppsTemplates } from '@hooks/platformApps';
import { isString, makeAppParamsSchema } from '@utils';

import { ChaseSpinner } from '@components/Shared';

import { PlatformAppManagementParamsSchema } from './PlatformAppManagementParamsSchema';

type Props = {
  type: 'input' | 'output';
  templateVersion: string;
  templateName: string;
  params:
    | OutletContext.PlatformAppsInstance['input']
    | OutletContext.PlatformAppsInstance['output'];
};
export const PlatformAppManagementParamsSection: React.FC<Props> = ({
  type,
  templateVersion,
  templateName,
  params,
}) => {
  const { isFetched, template } = usePlatformAppsTemplates({
    version: templateVersion,
    appName: templateName,
  });

  const schema = template?.[type];

  const paramsSchema = useMemo(() => {
    if (schema) {
      return makeAppParamsSchema(schema);
    }

    return null;
  }, [schema]);

  const renderContent = () => {
    if (!isFetched || !params) {
      return (
        <div className="my-6 flex justify-center">
          <ChaseSpinner color="black" className="h-10 w-10" />
        </div>
      );
    }

    if (!template) {
      return <p>App template was not found for this version</p>;
    }

    if (!paramsSchema) {
      return <p>Could not extract app schema</p>;
    }

    if (isString(params)) {
      return <p>{params}</p>;
    }

    return (
      <div className="flex flex-col gap-10">
        <PlatformAppManagementParamsSchema
          isTopLevel
          schema={params}
          params={paramsSchema}
        />
      </div>
    );
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <h3 className="text-h5 capitalize">{type}</h3>
      <div className="-my-2 h-px bg-neural-03" />
      {renderContent()}
    </div>
  );
};
